import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap';
import { loadStoreData, comparePrices } from '../services/priceService';
import ComparisonResults from './ComparisonResults';

const PriceComparison = ({ store1Id, store2Id, store1Name, store2Name }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [comparisonData, setComparisonData] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState({
    store1: false,
    store2: false,
    downloading: false
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        setComparisonData([]);
        
        console.log(`Starting to fetch data for stores ${store1Id} and ${store2Id}`);
        
        // Load data for both stores in parallel
        setLoadingProgress(prev => ({ ...prev, store1: true, store2: true }));
        try {
          const [store1Data, store2Data] = await Promise.all([
            loadStoreData(store1Id),
            loadStoreData(store2Id)
          ]);
          setLoadingProgress(prev => ({ ...prev, store1: false, store2: false }));

          // Compare prices
          const comparedData = comparePrices(store1Data, store2Data);
          setComparisonData(comparedData);
          setLoading(false);
        } catch (err) {
          // Check if this is a 404 error (store data not found)
          if (err.response && err.response.status === 404) {
            setLoadingProgress(prev => ({ ...prev, downloading: true }));

            // Try again after a delay to give the server time to download the data
            setTimeout(async () => {
              try {
                setLoadingProgress(prev => ({ ...prev, store1: true, store2: true, downloading: false }));
                const [store1Data, store2Data] = await Promise.all([
                  loadStoreData(store1Id),
                  loadStoreData(store2Id)
                ]);
                setLoadingProgress(prev => ({ ...prev, store1: false, store2: false }));

                // Compare prices
                const comparedData = comparePrices(store1Data, store2Data);
                setComparisonData(comparedData);
                setLoading(false);
              } catch (retryErr) {
                setError('Failed to load store data after download attempt. Please try again later.');
                setLoading(false);
              }
            }, 5000); // Wait 5 seconds before retrying
          } else {
            setError('Failed to load and compare store data. Please try again later.');
            setLoading(false);
          }
        }
      } catch (err) {
        console.error('Error in fetch data:', err);
        setError('Failed to load and compare store data. Please try again later.');
        setLoading(false);
      }
    };

    if (store1Id && store2Id) {
      fetchData();
    }
  }, [store1Id, store2Id]);

  if (loading) {
    return (
      <Container className="text-center my-5">
        <Spinner animation="border" role="status" variant="primary" />
        <p className="mt-3">
          {loadingProgress.downloading && (
            <>
              <span className="text-warning">Downloading store data. This may take a few minutes...</span>
              <br />
              <small className="text-muted">First time comparing these stores? We need to download their price data.</small>
            </>
          )}
          {!loadingProgress.downloading && loadingProgress.store1 && `Loading data for ${store1Name}...`}
          {!loadingProgress.downloading && loadingProgress.store2 && `Loading data for ${store2Name}...`}
          {!loadingProgress.downloading && !loadingProgress.store1 && !loadingProgress.store2 && 'Comparing prices...'}
        </p>
      </Container>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
      </Alert>
    );
  }

  return (
    <Row>
      <Col>
        <ComparisonResults
          comparisonData={comparisonData}
          store1Name={store1Name}
          store2Name={store2Name}
          store1Id={store1Id}
          store2Id={store2Id}
        />
      </Col>
    </Row>
  );
};

export default PriceComparison;
